/* Checks advertised gift savings against Shopify's own cart response. Never changes a cart or a discount. */
(() => {
  'use strict';
  if (window.DivineOfferCartCheck) return;
  const storageKey = 'divine-offer-cart-check:v1';
  const rules = { b2g1: { paid: 2, free: 1 }, b2g2: { paid: 2, free: 2 }, b3g2: { paid: 3, free: 2 } };
  let context = null;
  let revision = 0;
  let state = { status: 'idle', actualTotal: null, currency: null };

  function normalize(config) {
    if (!config || !Object.prototype.hasOwnProperty.call(rules, config.mode) || !Array.isArray(config.eligibleProductIds) || !Array.isArray(config.giftProductIds)) return null;
    const ids = [...new Set(config.eligibleProductIds.filter(id => typeof id === 'string' || typeof id === 'number').map(String).filter(id => /^[1-9]\d*$/.test(id)))];
    const gifts = [...new Set(config.giftProductIds.filter(id => typeof id === 'string' || typeof id === 'number').map(String).filter(id => ids.includes(id)))];
    return ids.length && ids.length <= 250 && gifts.length ? { mode: config.mode, eligibleProductIds: ids, giftProductIds: gifts } : null;
  }
  function persist() {
    try {
      if (context) window.sessionStorage.setItem(storageKey, JSON.stringify(context));
      else window.sessionStorage.removeItem(storageKey);
    } catch (_) { /* Private browsing can disable storage; the current page still works. */ }
  }
  function money(amount, currency) {
    try { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(amount / 100); }
    catch (_) { return currency + ' ' + (amount / 100).toFixed(2); }
  }
  function paint() {
    const visible = state.status === 'unapplied' || state.status === 'unknown' || state.status === 'checking';
    for (const panel of document.querySelectorAll('[data-dd-offer-cart-status]')) {
      panel.hidden = !visible;
      panel.dataset.offerCheck = state.status;
      const message = panel.querySelector('[data-dd-offer-cart-message]');
      let text = state.status === 'checking' ? 'Checking your gift savings…' : state.status === 'unknown' ? 'We couldn’t confirm your gift savings. Review your bag total before checkout.' : 'Your gift savings haven’t applied yet. Review your bag total before checkout.';
      if (state.status !== 'checking' && Number.isSafeInteger(state.actualTotal) && state.currency) text += ' Bag total: ' + money(state.actualTotal, state.currency) + '.';
      if (message) message.textContent = visible ? text : '';
      const refresh = panel.querySelector('[data-dd-offer-cart-refresh]');
      if (refresh) refresh.disabled = state.status === 'checking';
    }
    return { ...state };
  }
  function isCart(cart) {
    return !!cart && Array.isArray(cart.items) && Number.isSafeInteger(cart.total_price) && cart.total_price >= 0 && typeof cart.currency === 'string' && /^[A-Z]{3}$/.test(cart.currency);
  }
  function evaluate(cart, config) {
    if (!isCart(cart)) throw new Error('Incomplete cart response');
    const base = { actualTotal: cart.total_price, currency: cart.currency, expectedSavings: 0, actualSavings: 0, eligibleCount: 0 };
    if (!cart.items.length) return { ...base, status: 'empty' };
    const eligible = new Set(config.eligibleProductIds);
    const gifts = new Set(config.giftProductIds);
    const lots = [];
    for (const line of cart.items) {
      if (!eligible.has(String(line.product_id)) || line.gift_card || line.selling_plan_allocation || line.selling_plan) continue;
      const quantity = line.quantity, original = line.original_line_price, final = line.final_line_price;
      if (!Number.isSafeInteger(quantity) || quantity < 1 || !Number.isSafeInteger(original) || original < 0 || !Number.isSafeInteger(final) || final < 0 || final > original) throw new Error('Incomplete line prices');
      const unitPrice = original / quantity;
      if (!Number.isSafeInteger(unitPrice)) throw new Error('Cannot determine original unit price');
      if (gifts.has(String(line.product_id))) lots.push({ price: unitPrice, quantity });
      base.eligibleCount += quantity;
      base.actualSavings += original - final;
    }
    const rule = rules[config.mode];
    let freeCount = Math.min(Math.floor(base.eligibleCount / (rule.paid + rule.free)) * rule.free, lots.reduce((sum, lot) => sum + lot.quantity, 0));
    if (!freeCount) return { ...base, status: 'not_qualified' };
    for (const lot of lots.sort((a, b) => a.price - b.price)) {
      const discounted = Math.min(freeCount, lot.quantity);
      base.expectedSavings += discounted * lot.price;
      freeCount -= discounted;
      if (!freeCount) break;
    }
    // Other compatible or better-value product savings can satisfy the amount.
    // Matching an amount does not identify or certify a particular campaign.
    return { ...base, status: base.actualSavings >= base.expectedSavings ? 'savings_present' : 'unapplied' };
  }
  async function verify(config, cart) {
    const next = normalize(config === undefined ? context : config);
    const requestRevision = ++revision;
    if (!next) {
      context = null; persist(); state = { status: 'idle', actualTotal: null, currency: null }; return paint();
    }
    context = next; persist();
    state = { status: 'checking', actualTotal: null, currency: null }; paint();
    try {
      if (cart === undefined) {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(root.replace(/\/?$/, '/') + 'cart.js', { method: 'GET', credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Cart read failed');
        cart = await response.json();
      }
      const result = evaluate(cart, next);
      if (requestRevision !== revision) return { ...state };
      state = result;
      if (result.status === 'empty') { context = null; persist(); }
    } catch (_) {
      if (requestRevision !== revision) return { ...state };
      state = { status: 'unknown', actualTotal: isCart(cart) ? cart.total_price : null, currency: isCart(cart) ? cart.currency : null };
    }
    return paint();
  }
  function start() {
    document.addEventListener('click', event => {
      const refresh = event.target.closest?.('[data-dd-offer-cart-refresh]');
      if (refresh && !refresh.disabled && context) verify(context);
    });
    document.addEventListener('shopify:section:load', paint);
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
        if (!context || window.DivineCartGifts?.handlesCartChecks()) return paint();
        // The set builder has just awaited verify() before publishing this event.
        if (event?.source === 'divine-offer-builder' || event?.source === 'divine-cart-gifts') return paint();
        return verify(context, isCart(event?.cartData) ? event.cartData : undefined);
      });
    }
    if (context && state.status === 'idle') verify(context);
    else paint();
  }
  try { context = normalize(JSON.parse(window.sessionStorage.getItem(storageKey) || 'null')); }
  catch (_) { context = null; }
  window.DivineOfferCartCheck = { verify, paint };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
